import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import SEOHead from '../../../components/SEOHead';
import QuoteFormWidget from '../../../components/feature/QuoteFormWidget';

const EVChargerBlogPost = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'EV Charger Installation Guide for Fresno Homeowners',
    description: 'Everything Fresno homeowners need to know about Level 2 EV charger installation — costs, permits, rebates, and choosing the right electrician in Fresno, CA.',
    datePublished: '2025-02-28',
    dateModified: '2025-02-28',
    author: { '@type': 'Organization', name: 'Electrician Solutions Fresno' },
    publisher: { '@type': 'Organization', name: 'Electrician Solutions Fresno', url: 'https://electriciansolutionsfresno.com' },
    mainEntityOfPage: 'https://electriciansolutionsfresno.com/blog/ev-charger-installation-fresno-homeowners',
  };

  return (
    <>
      <SEOHead
        title="EV Charger Installation Fresno CA | Level 2 Home Charger Guide | Electrician Solutions"
        description="Everything Fresno homeowners need to know about Level 2 EV charger installation — costs, permits, rebates, and choosing the right electrician in Fresno, CA."
        canonical="/blog/ev-charger-installation-fresno-homeowners"
        keywords="EV charger installation Fresno, Level 2 home charger Fresno CA, Tesla charger Fresno, electric vehicle charging Fresno"
        schema={schema}
      />
      <div className="min-h-screen bg-white">
        <Navbar />

        <section className="pt-28 pb-0 bg-[#1a1a1a]">
          <div className="max-w-[900px] mx-auto px-6 pt-6 pb-10">
            <nav className="flex items-center gap-2 text-xs text-gray-500 mb-6">
              <Link to="/" className="hover:text-[#FFD700] transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/blog" className="hover:text-[#FFD700] transition-colors">Blog</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-gray-400">EV & Solar</span>
            </nav>
            <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-4">EV & Solar</span>
            <h1 className="text-3xl sm:text-5xl font-black text-white mb-5 leading-tight">
              EV Charger Installation Guide for Fresno Homeowners
            </h1>
            <div className="flex items-center gap-4 text-gray-500 text-sm">
              <span>February 28, 2025</span><span>·</span><span>7 min read</span><span>·</span>
              <span className="text-[#FFD700]">Electrician Solutions Fresno</span>
            </div>
          </div>
        </section>

        <section className="max-w-[900px] mx-auto px-6 -mt-2">
          <div className="rounded-2xl overflow-hidden h-72 sm:h-[440px]">
            <img
              src="https://public.readdy.ai/ai/img_res/64211972bf37941f140084731a285aa7.jpg"
              alt="Level 2 EV charger installation Fresno CA"
              className="w-full h-full object-cover"
            />
          </div>
        </section>

        <article className="max-w-[900px] mx-auto px-6 py-12">
          <div className="prose prose-lg max-w-none">

            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              With California's push toward zero-emission vehicles and over 270 sunny days in Fresno, pairing an EV with solar and a home Level 2 charger makes incredible financial sense. But installing a charger isn't just plugging into the wall — it requires a licensed Fresno electrician who understands panel capacity, California permits, and PG&E coordination.
            </p>

            <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">Level 1 vs Level 2: What's the Difference?</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {[
                { type: 'Level 1 (Standard 120V)', range: '3–5 miles of range per hour', plug: 'Standard household outlet', cost: 'No installation cost', note: 'Too slow for most Fresno EV drivers' },
                { type: 'Level 2 (240V)', range: '20–35 miles of range per hour', plug: 'Dedicated 240V circuit', cost: '$500–$1,500 installed', note: 'Recommended for all Fresno homes' },
              ].map((item, i) => (
                <div key={i} className={`p-5 rounded-xl border-2 ${i === 1 ? 'border-[#FFD700] bg-[#fffbea]' : 'border-gray-200 bg-[#f5f5f5]'}`}>
                  <div className={`text-xs font-black uppercase tracking-widest mb-2 ${i === 1 ? 'text-[#1a1a1a]' : 'text-gray-500'}`}>{item.type}</div>
                  <ul className="space-y-1 text-sm text-gray-600">
                    <li><strong>Range:</strong> {item.range}</li>
                    <li><strong>Plug:</strong> {item.plug}</li>
                    <li><strong>Cost:</strong> {item.cost}</li>
                    <li className={`font-bold ${i === 1 ? 'text-[#1a1a1a]' : 'text-gray-400'}`}>{item.note}</li>
                  </ul>
                </div>
              ))}
            </div>

            <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">What Does EV Charger Installation Cost in Fresno?</h2>
            <div className="overflow-x-auto mb-8">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-[#1a1a1a] text-white">
                    <th className="text-left px-4 py-3 font-bold rounded-tl-lg">Scenario</th>
                    <th className="text-left px-4 py-3 font-bold rounded-tr-lg">Fresno Cost Estimate</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Panel has capacity, short run to garage', '$500 – $800'],
                    ['Panel needs 50A circuit added, medium run', '$800 – $1,200'],
                    ['Panel upgrade required before install', '$2,000 – $3,500 total'],
                    ['Tesla Wall Connector (NACS) installation', '$600 – $1,100'],
                    ['Commercial multi-unit EV charger setup', '$3,000 – $8,000+'],
                  ].map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#f5f5f5]'}>
                      <td className="px-4 py-3 font-medium text-[#1a1a1a]">{row[0]}</td>
                      <td className="px-4 py-3 text-gray-600">{row[1]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">California EV Charger Incentives Available to Fresno Homeowners</h2>
            <div className="space-y-3 mb-8">
              {[
                { prog: 'Federal Tax Credit (30E)', val: 'Up to $1,000 (30% of equipment + install cost)', note: 'Claim on your IRS Form 8911' },
                { prog: 'PG&E EV Charge Network', val: 'Reduced electricity rates for EV charging', note: 'Enroll in EV2-A or EV-B rate plan' },
                { prog: 'CALeVIP (Commercial)', val: 'Up to $4,500 per charger', note: 'Available for multi-unit housing and commercial' },
                { prog: 'Self-Generation Incentive Program', val: 'Pairs with battery storage', note: 'Pair your EV charger with home battery storage' },
              ].map((item, i) => (
                <div key={i} className="flex gap-4 p-4 bg-[#f5f5f5] rounded-xl">
                  <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center bg-[#FFD700] rounded-full">
                    <i className="ri-money-dollar-circle-fill text-[#1a1a1a] text-sm"></i>
                  </span>
                  <div>
                    <strong className="text-[#1a1a1a] font-black text-sm">{item.prog}</strong>
                    <p className="text-gray-600 text-sm mt-0.5">{item.val}</p>
                    <p className="text-gray-500 text-xs mt-0.5 italic">{item.note}</p>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">Our EV Charger Installation Process in Fresno</h2>
            <ol className="space-y-3 mb-8">
              {[
                'Free quote — we assess your panel capacity and route to garage/driveway',
                'Permit pulled from City of Fresno Building Department',
                'Dedicated 50A or 60A 240V circuit installed to charger location',
                'NEMA 14-50 outlet or hardwired charger mounted and tested',
                'PG&E coordination if panel upgrade is needed',
                'Final inspection and certification',
              ].map((step, i) => (
                <li key={i} className="flex gap-4 text-gray-600 text-sm">
                  <span className="w-7 h-7 flex-shrink-0 flex items-center justify-center bg-[#FFD700] text-[#1a1a1a] rounded-full font-black text-xs">{i + 1}</span>
                  {step}
                </li>
              ))}
            </ol>

            <h2 className="text-2xl font-black text-[#1a1a1a] mt-10 mb-4">Related EV & Solar Services in Fresno</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
              {[
                { label: 'EV Charger Installation', path: '/services/renewables-ev/ev-charger-installation', desc: 'Level 2 charger installation in Fresno, CA' },
                { label: 'Tesla Charger Installation', path: '/services/renewables-ev/tesla-charger-installation', desc: 'Tesla Wall Connector installation experts' },
                { label: 'Solar Panel Connections', path: '/services/renewables-ev/solar-panel-electrical-connections', desc: 'Connect your solar to your EV charger' },
                { label: 'Backup Battery Systems', path: '/services/renewables-ev/backup-battery-systems', desc: 'Powerwall and battery backup for Fresno homes' },
              ].map(s => (
                <Link key={s.path} to={s.path} className="flex items-start gap-4 p-4 border-2 border-gray-100 hover:border-[#FFD700] rounded-xl transition-all cursor-pointer group">
                  <span className="w-8 h-8 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-arrow-right-circle-fill text-[#FFD700] text-xl"></i>
                  </span>
                  <div>
                    <p className="font-black text-[#1a1a1a] text-sm">{s.label}</p>
                    <p className="text-gray-500 text-xs mt-0.5">{s.desc}</p>
                  </div>
                </Link>
              ))}
            </div>

          </div>

          <div className="mt-12 bg-[#1a1a1a] rounded-2xl p-8 sm:p-10">
            <h3 className="text-2xl font-black text-white mb-3">Ready to Install Your EV Charger in Fresno?</h3>
            <p className="text-gray-400 text-sm mb-6">Get a free quote from Fresno's licensed EV charger installation experts. We handle all permits, panel work, and PG&E coordination.</p>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-[#FFD700] text-[#1a1a1a] px-8 py-4 rounded-full font-black text-sm uppercase tracking-wide hover:bg-[#FFC700] transition-all cursor-pointer whitespace-nowrap">
              Get Free EV Charger Quote <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="mt-14">
            <h3 className="text-lg font-black text-[#1a1a1a] mb-6">More Fresno Electrical Guides</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                { title: 'Solar Electrical Connections: What Fresno Homeowners Must Know', slug: 'solar-electrical-connections-fresno', cat: 'EV & Solar' },
                { title: 'How to Know When to Upgrade Your Electrical Panel in Fresno, CA', slug: 'when-to-upgrade-electrical-panel-fresno', cat: 'Residential' },
              ].map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="p-5 border-2 border-gray-100 hover:border-[#FFD700] rounded-xl transition-all cursor-pointer group">
                  <span className="inline-block bg-[#FFD700] text-[#1a1a1a] text-xs font-black uppercase tracking-widest px-3 py-1 rounded-full mb-3">{p.cat}</span>
                  <p className="font-black text-[#1a1a1a] text-sm leading-snug">{p.title}</p>
                  <span className="text-[#FFD700] text-xs font-bold mt-2 flex items-center gap-1">Read <i className="ri-arrow-right-line"></i></span>
                </Link>
              ))}
            </div>
          </div>
        </article>

        <div className="max-w-[900px] mx-auto px-6 pb-16">
          <QuoteFormWidget />
        </div>
        <Footer />
      </div>
    </>
  );
};

export default EVChargerBlogPost;
